import type { ReactNode } from "react";
import { isLastNameOnlyPreferredName } from "@/lib/personHelpers";
import { formatPhoneNumber } from "@/lib/phoneHelpers";
import type { FacilityHit, PersonHit, SectionHit } from "./types";
import { formatLocationText } from "./utils";

type AlgoliaHighlight = {
  value?: string;
  matchLevel?: "none" | "partial" | "full" | string;
};

export type ListItemContent = string | JSX.Element | null | undefined;

export type VisiblePersonAttribute = "pga_id" | "phone" | "email" | "section_name" | "facility_name";

const HIGHLIGHT_MARK_CLASS = "rounded-sm bg-sys-brand-secondary-container text-sys-brand-on-secondary-container";

function isAlgoliaMatch(highlight?: AlgoliaHighlight | null): boolean {
  return highlight?.matchLevel === "partial" || highlight?.matchLevel === "full";
}

function asHighlightList(value: unknown): AlgoliaHighlight[] {
  if (!value) return [];
  return Array.isArray(value) ? value : [value as AlgoliaHighlight];
}

export function getMatchedHighlights(
  highlightResult: Record<string, unknown> | null | undefined,
  attribute: string
): AlgoliaHighlight[] {
  if (!highlightResult) return [];
  return asHighlightList(highlightResult[attribute]).filter(isAlgoliaMatch);
}

export function stripHighlightTags(value: string): string {
  return value.replace(/<\/?em>/g, "");
}

export function renderAlgoliaHighlight(highlightedValue: string | null | undefined): ListItemContent {
  if (!highlightedValue) return null;

  const nodes: ReactNode[] = [];
  const tokenPattern = /<\/?em>/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let inHighlight = false;
  let key = 0;

  while ((match = tokenPattern.exec(highlightedValue)) !== null) {
    const text = highlightedValue.slice(lastIndex, match.index);
    if (text) {
      nodes.push(
        inHighlight ? (
          <mark key={key++} className={HIGHLIGHT_MARK_CLASS}>
            {text}
          </mark>
        ) : (
          <span key={key++}>{text}</span>
        )
      );
    }

    inHighlight = match[0] === "<em>";
    lastIndex = match.index + match[0].length;
  }

  const trailing = highlightedValue.slice(lastIndex);
  if (trailing) {
    nodes.push(
      inHighlight ? (
        <mark key={key++} className={HIGHLIGHT_MARK_CLASS}>
          {trailing}
        </mark>
      ) : (
        <span key={key++}>{trailing}</span>
      )
    );
  }

  if (nodes.length === 0) return highlightedValue;
  return <span>{nodes}</span>;
}

function markWhole(content: ReactNode): JSX.Element {
  return <mark className={HIGHLIGHT_MARK_CLASS}>{content}</mark>;
}

type DigitHighlight = { digit: string; highlighted: boolean };

function digitHighlightsFromAlgolia(highlightedValue: string): DigitHighlight[] {
  const flags: DigitHighlight[] = [];
  let inHighlight = false;
  const tokenPattern = /<\/?em>|./g;
  let match: RegExpExecArray | null;

  while ((match = tokenPattern.exec(highlightedValue)) !== null) {
    const token = match[0];
    if (token === "<em>") {
      inHighlight = true;
      continue;
    }
    if (token === "</em>") {
      inHighlight = false;
      continue;
    }
    if (/\d/.test(token)) {
      flags.push({ digit: token, highlighted: inHighlight });
    }
  }

  return flags;
}

function digitHighlightsFromQuery(phoneDigits: string, query: string): DigitHighlight[] | null {
  const queryDigits = digitsOnly(query);
  if (queryDigits.length < 2) return null;

  const index = phoneDigits.indexOf(queryDigits);
  if (index < 0) return null;

  return phoneDigits.split("").map((digit, i) => ({
    digit,
    highlighted: i >= index && i < index + queryDigits.length
  }));
}

function alignDigitHighlights(flags: DigitHighlight[], formatted: string): DigitHighlight[] {
  const formattedDigits = digitsOnly(formatted);
  if (!formattedDigits) return [];

  const flagDigits = flags.map((flag) => flag.digit).join("");
  const start = flagDigits.indexOf(formattedDigits);
  if (start >= 0) return flags.slice(start, start + formattedDigits.length);
  if (flagDigits.endsWith(formattedDigits)) {
    return flags.slice(flags.length - formattedDigits.length);
  }
  return formattedDigits.split("").map((digit) => ({ digit, highlighted: false }));
}

function renderFormattedPhoneWithHighlights(formatted: string, flags: DigitHighlight[]): ListItemContent {
  if (!flags.some((flag) => flag.highlighted)) return formatted;

  const nodes: ReactNode[] = [];
  let digitIndex = 0;
  let buffer = "";
  let bufferHighlighted: boolean | null = null;
  let key = 0;

  const flush = () => {
    if (!buffer) return;
    nodes.push(
      bufferHighlighted ? (
        <mark key={key++} className={HIGHLIGHT_MARK_CLASS}>
          {buffer}
        </mark>
      ) : (
        <span key={key++}>{buffer}</span>
      )
    );
    buffer = "";
  };

  const nextDigitHighlighted = (fromIndex: number): boolean | null => {
    for (let i = fromIndex + 1; i < formatted.length; i++) {
      if (/\d/.test(formatted[i])) {
        return digitIndex < flags.length ? flags[digitIndex].highlighted : null;
      }
    }
    return null;
  };

  for (let i = 0; i < formatted.length; i++) {
    const ch = formatted[i];
    if (/\d/.test(ch) && digitIndex < flags.length) {
      const highlighted = flags[digitIndex].highlighted;
      digitIndex += 1;
      if (bufferHighlighted !== null && bufferHighlighted !== highlighted) flush();
      bufferHighlighted = highlighted;
      buffer += ch;
    } else {
      if (bufferHighlighted === null) {
        // Adopt the next digit's highlight so leading "(" stays with the number.
        bufferHighlighted = nextDigitHighlighted(i - 1) === true;
      } else {
        const keepWithHighlight = bufferHighlighted === true && nextDigitHighlighted(i) === true;
        if (bufferHighlighted === true && !keepWithHighlight) {
          flush();
          bufferHighlighted = false;
        }
      }
      buffer += ch;
    }
  }
  flush();

  return <span>{nodes}</span>;
}

function highlightFormattedPhone(
  phone: string,
  options: { highlightedValue?: string; query?: string } = {}
): ListItemContent {
  const formatted = formatPhoneNumber(phone) || phone;
  const phoneDigits = digitsOnly(phone);

  const fromQuery = options.query ? digitHighlightsFromQuery(phoneDigits, options.query) : null;
  const fromAlgolia =
    !fromQuery && options.highlightedValue?.includes("<em>")
      ? digitHighlightsFromAlgolia(options.highlightedValue)
      : null;
  const flags = fromQuery || fromAlgolia;
  if (!flags) return formatted;

  return renderFormattedPhoneWithHighlights(formatted, alignDigitHighlights(flags, formatted));
}

function line(content: ReactNode, key: string): JSX.Element {
  return (
    <div key={key} className="min-w-0">
      {content}
    </div>
  );
}

function highlightedOrPlain(
  highlightResult: Record<string, unknown> | undefined,
  attribute: string,
  plain: string
): ListItemContent {
  const matched = getMatchedHighlights(highlightResult, attribute)[0];
  if (matched?.value) return renderAlgoliaHighlight(matched.value);
  return plain;
}

function getMatchedPhoneHighlight(
  hit: Pick<PersonHit, "phone" | "phones" | "_highlightResult">
): { raw: string; highlightedValue: string } | null {
  const highlightResult = hit._highlightResult;
  const fromPhones = getMatchedHighlights(highlightResult, "phones")[0];
  if (fromPhones?.value) {
    return { raw: stripHighlightTags(fromPhones.value), highlightedValue: fromPhones.value };
  }

  const fromPhone = getMatchedHighlights(highlightResult, "phone")[0];
  if (fromPhone?.value) {
    return { raw: stripHighlightTags(fromPhone.value), highlightedValue: fromPhone.value };
  }

  return null;
}

function digitsOnly(value: string | null | undefined): string {
  return (value || "").replace(/\D/g, "");
}

function phonesEqual(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!a || !b) return false;
  return digitsOnly(a) === digitsOnly(b);
}

function allHitPhones(hit: { phone?: string; phones?: string[] }): string[] {
  return [...(hit.phones || []), hit.phone].filter((value): value is string => Boolean(value));
}

function findPhoneMatchingQuery(hit: { phone?: string; phones?: string[] }, query: string): string | null {
  const queryDigits = digitsOnly(query);
  if (queryDigits.length < 2) return null;

  return allHitPhones(hit).find((phone) => digitsOnly(phone).includes(queryDigits)) || null;
}

function fieldContainsQuery(value: string | null | undefined, query: string): boolean {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery || !value) return false;
  return value.toLowerCase().includes(normalizedQuery);
}

const PERSON_MEANINGFUL_HIGHLIGHT_ATTRS = [
  "name",
  "preferred_name",
  "pga_id",
  "phone",
  "phones",
  "email",
  "facility_name",
  "section_name"
] as const;

export function isPersonCrmIdOnlyMatch(hit: PersonHit, query?: string): boolean {
  if (hit.object_type !== "Person") return false;

  const highlightResult = hit._highlightResult;
  if (PERSON_MEANINGFUL_HIGHLIGHT_ATTRS.some((attr) => getMatchedHighlights(highlightResult, attr).length > 0)) {
    return false;
  }

  const normalizedQuery = query?.trim().toLowerCase() || "";
  if (normalizedQuery && query) {
    if (findPhoneMatchingQuery(hit, query)) return false;
    if (fieldContainsQuery(hit.email, normalizedQuery)) return false;
    if (fieldContainsQuery(hit.pga_id, normalizedQuery)) return false;
    if (fieldContainsQuery(hit.name, normalizedQuery)) return false;
    if (fieldContainsQuery(hit.preferred_name, normalizedQuery)) return false;
    if (fieldContainsQuery(hit.facility_name, normalizedQuery)) return false;
    if (fieldContainsQuery(hit.section_name, normalizedQuery)) return false;
  }

  const legacyHighlighted = getMatchedHighlights(highlightResult, "legacy_id").length > 0;
  const legacyContainsQuery = normalizedQuery ? fieldContainsQuery(hit.legacy_id, normalizedQuery) : false;
  return legacyHighlighted || legacyContainsQuery;
}

function highlightQueryInValue(value: string, query?: string): ListItemContent {
  const needle = query?.trim();
  if (!needle) return value;

  const lowerValue = value.toLowerCase();
  const lowerNeedle = needle.toLowerCase();
  const index = lowerValue.indexOf(lowerNeedle);
  if (index < 0) return markWhole(value);

  return (
    <span>
      {value.slice(0, index)}
      <mark className={HIGHLIGHT_MARK_CLASS}>{value.slice(index, index + needle.length)}</mark>
      {value.slice(index + needle.length)}
    </span>
  );
}

export function getHiddenMatchFallbackLine(
  hit: {
    phone?: string;
    phones?: string[];
    email?: string;
    legacy_id?: string;
    section_id?: string;
    object_type?: string;
    _highlightResult?: Record<string, unknown>;
  },
  visibleAttributes: readonly VisiblePersonAttribute[],
  query?: string
): ListItemContent {
  const highlightResult = hit._highlightResult;
  const phoneVisible = visibleAttributes.includes("phone");
  const normalizedQuery = query?.trim().toLowerCase() || "";

  const matchedPhone = getMatchedPhoneHighlight(hit);
  const matchedPhoneRaw = matchedPhone?.raw || (query ? findPhoneMatchingQuery(hit, query) : null);
  if (matchedPhoneRaw) {
    const matchedVisiblePhone = phoneVisible && phonesEqual(matchedPhoneRaw, hit.phone);
    if (!matchedVisiblePhone) {
      return highlightFormattedPhone(matchedPhoneRaw, {
        highlightedValue: matchedPhone?.highlightedValue,
        query
      });
    }
  }

  if (!visibleAttributes.includes("email")) {
    const emailMatch = getMatchedHighlights(highlightResult, "email")[0];
    if (emailMatch?.value) {
      return renderAlgoliaHighlight(emailMatch.value);
    }
    if (normalizedQuery && hit.email && fieldContainsQuery(hit.email, normalizedQuery)) {
      return highlightQueryInValue(hit.email, query);
    }
  }

  if (hit.object_type === "Facility") {
    const legacyMatch = getMatchedHighlights(highlightResult, "legacy_id")[0];
    if (legacyMatch?.value) {
      return <>ID: {renderAlgoliaHighlight(legacyMatch.value)}</>;
    }
    if (normalizedQuery && hit.legacy_id && fieldContainsQuery(hit.legacy_id, normalizedQuery)) {
      return <>ID: {highlightQueryInValue(hit.legacy_id, query)}</>;
    }
  }

  if (hit.object_type === "Section") {
    const legacyMatch = getMatchedHighlights(highlightResult, "legacy_id")[0];
    if (legacyMatch?.value) {
      return renderAlgoliaHighlight(legacyMatch.value);
    }
    if (normalizedQuery && hit.legacy_id && fieldContainsQuery(hit.legacy_id, normalizedQuery)) {
      return highlightQueryInValue(hit.legacy_id, query);
    }
  }

  const sectionIdMatch = getMatchedHighlights(highlightResult, "section_id")[0];
  if (sectionIdMatch?.value) {
    return renderAlgoliaHighlight(sectionIdMatch.value);
  }
  if (normalizedQuery && hit.section_id && fieldContainsQuery(hit.section_id, normalizedQuery)) {
    return highlightQueryInValue(hit.section_id, query);
  }

  return null;
}

export type PersonSupportingMatchOptions = {
  visibleAttributes?: readonly VisiblePersonAttribute[];
  showContactDetails?: boolean;
  query?: string;
};

export function getPersonMatchSupportingContent(
  hit: PersonHit,
  options: PersonSupportingMatchOptions = {}
): JSX.Element | undefined {
  const visibleAttributes: readonly VisiblePersonAttribute[] =
    options.visibleAttributes ??
    (options.showContactDetails
      ? ["pga_id", "section_name", "facility_name", "phone", "email"]
      : ["pga_id", "section_name", "facility_name"]);

  const highlightResult = hit._highlightResult;
  const lines: JSX.Element[] = [];

  for (const attribute of visibleAttributes) {
    switch (attribute) {
      case "pga_id":
        if (hit.pga_id) {
          lines.push(line(<>PGA ID: {highlightedOrPlain(highlightResult, "pga_id", hit.pga_id)}</>, "pga_id"));
        }
        break;
      case "section_name":
        if (hit.section_name) {
          lines.push(line(highlightedOrPlain(highlightResult, "section_name", hit.section_name), "section_name"));
        }
        break;
      case "facility_name":
        if (hit.facility_name) {
          lines.push(line(highlightedOrPlain(highlightResult, "facility_name", hit.facility_name), "facility_name"));
        }
        break;
      case "phone":
        if (hit.phone) {
          const matchedPhone = getMatchedPhoneHighlight(hit);
          const matchedPhoneRaw =
            matchedPhone?.raw || (options.query ? findPhoneMatchingQuery(hit, options.query) : null);
          const matchedVisiblePhone = phonesEqual(matchedPhoneRaw, hit.phone);
          lines.push(
            line(
              matchedVisiblePhone
                ? highlightFormattedPhone(hit.phone, {
                    highlightedValue: matchedPhone?.highlightedValue,
                    query: options.query
                  })
                : formatPhoneNumber(hit.phone),
              "phone"
            )
          );
        }
        break;
      case "email":
        if (hit.email) {
          lines.push(line(highlightedOrPlain(highlightResult, "email", hit.email), "email"));
        }
        break;
    }
  }

  if (!hit.section_name && !hit.facility_name && hit.governance_role_title && hit.governance_facility_name) {
    lines.push(line(`${hit.governance_role_title} • ${hit.governance_facility_name}`, "governance"));
  }

  const fallback = getHiddenMatchFallbackLine(hit, visibleAttributes, options.query);
  if (fallback) {
    lines.push(line(fallback, "fallback"));
  }

  if (lines.length === 0) return undefined;
  return <div className="flex flex-col gap-0.5">{lines}</div>;
}

export function getPersonMatchHeadline(hit: PersonHit, displayName: string): string | JSX.Element {
  const highlightResult = hit._highlightResult;
  const preferred = getMatchedHighlights(highlightResult, "preferred_name")[0];
  if (preferred?.value && !isLastNameOnlyPreferredName(hit.name, hit.preferred_name)) {
    return renderAlgoliaHighlight(preferred.value) ?? displayName;
  }

  const name = getMatchedHighlights(highlightResult, "name")[0];
  if (name?.value) {
    return renderAlgoliaHighlight(name.value) ?? displayName;
  }

  return displayName;
}

export function getFacilityMatchHeadline(hit: FacilityHit): string | JSX.Element {
  const highlightResult = hit._highlightResult;
  const fallback = hit.facility_name || hit.name;
  const facilityName = getMatchedHighlights(highlightResult, "facility_name")[0];
  if (facilityName?.value) return renderAlgoliaHighlight(facilityName.value) ?? fallback;
  const name = getMatchedHighlights(highlightResult, "name")[0];
  if (name?.value) return renderAlgoliaHighlight(name.value) ?? fallback;
  return fallback;
}

export function getFacilityMatchSupportingContent(hit: FacilityHit, query?: string): JSX.Element | undefined {
  const highlightResult = hit._highlightResult;
  const lines: JSX.Element[] = [];

  if (hit.section_name) {
    const sectionHighlight = getMatchedHighlights(highlightResult, "section_name")[0];
    lines.push(
      <div key="section" className="min-w-0">
        {sectionHighlight?.value ? renderAlgoliaHighlight(sectionHighlight.value) : hit.section_name}
      </div>
    );
  }

  const location = formatLocationText(hit.city, hit.state);
  if (location) {
    lines.push(
      <div key="location" className="min-w-0">
        {location}
      </div>
    );
  }

  if (hit.phone) {
    const matchedPhone = getMatchedPhoneHighlight(hit);
    const matchedPhoneRaw = matchedPhone?.raw || (query ? findPhoneMatchingQuery(hit, query) : null);
    const matchedVisiblePhone = phonesEqual(matchedPhoneRaw, hit.phone);
    lines.push(
      line(
        matchedVisiblePhone
          ? highlightFormattedPhone(hit.phone, {
              highlightedValue: matchedPhone?.highlightedValue,
              query
            })
          : formatPhoneNumber(hit.phone),
        "phone"
      )
    );
  }

  const fallback = getHiddenMatchFallbackLine(hit, ["section_name", "phone"], query);
  if (fallback) {
    lines.push(line(fallback, "fallback"));
  }

  if (lines.length === 0) return undefined;
  return <div className="flex flex-col gap-0.5">{lines}</div>;
}

export function getSectionMatchHeadline(hit: SectionHit): ListItemContent {
  const highlightResult = hit._highlightResult;
  const sectionHighlight =
    getMatchedHighlights(highlightResult, "section_name")[0] ?? getMatchedHighlights(highlightResult, "name")[0];
  if (sectionHighlight?.value) {
    return renderAlgoliaHighlight(sectionHighlight.value);
  }
  return hit.section_name || hit.name;
}
